import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from, of } from "rxjs";
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { ApplicationHttpService } from "src/app/application/shared/services/backend/application-http.service";
import { QueryStringParameterKey } from "src/app/shared/routes/shared/shared-route.constant";
import {
  QueryStringParameter,
  RouterService,
} from "src/app/shared/services/router.service";
import { AppState } from "src/app/shared/store/app.state";
import * as ApplicationActions from "src/app/shared/store/application/application.actions";

import { applicationLimitedOfferPagePath } from "../../routes/application/term-life-insurance/application-route.constant";
import { ExceptionResponseDto } from "../../services/backend/shared/exceptions/exception-response.dto";
import { SignDocumentUrlService } from "../../services/sign-document-url.service";
import { StoreUpToDateService } from "../../services/store-up-to-date.service";
import * as QuoteActions from "../quote/term-life-insurance/quote.actions";
import * as QuoteSelectors from "../quote/term-life-insurance/quote.selectors";

import { ApplicationMapperService } from "src/app/application/shared/services/backend/mapper/application-mapper.service";
import * as ApplicationSelectors from "./application.selectors";

@Injectable()
export class ApplicationEffects {
  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _routerService: RouterService,
    private readonly _applicationMapperService: ApplicationMapperService,
    private readonly _applicationHttpService: ApplicationHttpService,
    private readonly _signDocumentUrlService: SignDocumentUrlService,
  ) {}

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Business Actions (those type of actions are initially dispatched)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  createApplication$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ApplicationActions.createApplication),
      switchMap((action) => {
        return of(ApplicationActions.createApplicationStart(action));
      }),
    ),
  );

  getApplication$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ApplicationActions.getApplication),
      withLatestFrom(
        this._store.select(ApplicationSelectors.selectApplicationId),
      ),
      switchMap(([action, existingApplicationId]) => {
        const isApplicationInStoreUpToDate = StoreUpToDateService.isUpToDate(
          existingApplicationId,
          action.applicationId,
        );

        return isApplicationInStoreUpToDate
          ? of(ApplicationActions.getApplicationCancel())
          : of(ApplicationActions.getApplicationStart(action));
      }),
    ),
  );

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Chain Reaction Actions (those type of actions are never directly dispatched but are triggered as a result of a business action)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  startCreateApplication$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ApplicationActions.createApplicationStart),
      switchMap((action) => {
        const successExitPoint =
          this._signDocumentUrlService.createSuccessExitPointUrl();

        const errorExitPoint =
          this._signDocumentUrlService.createErrorExitPointUrl();

        const declineExitPoint =
          this._signDocumentUrlService.createDeclineExitPointUrl();

        const applicationQrCodeUrl =
          this._signDocumentUrlService.createApplicationQrCodeUrl();

        const requestDto =
          this._applicationMapperService.createCreateApplicationRequest(
            action.quoteId,
            successExitPoint,
            errorExitPoint,
            declineExitPoint,
            applicationQrCodeUrl,
          );

        return this._applicationHttpService
          .createApplication(action.product, requestDto)
          .pipe(
            map((response) =>
              ApplicationActions.createApplicationSuccess({
                ...response,
                originPage: action.originPage,
                nextNavigationPage: action.nextNavigationPage,
              }),
            ),
            catchError((error: ExceptionResponseDto) =>
              of(ApplicationActions.createApplicationFail(error)),
            ),
          );
      }),
    ),
  );

  startGetApplication$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ApplicationActions.getApplicationStart),
      switchMap((action) => {
        return this._applicationHttpService
          .getApplication(action.product, action.applicationId)
          .pipe(
            map((response) =>
              ApplicationActions.getApplicationSuccess(response),
            ),
            catchError((error: ExceptionResponseDto) =>
              of(ApplicationActions.getApplicationFail(error)),
            ),
          );
      }),
    ),
  );

  successApplication$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ApplicationActions.createApplicationSuccess),
      withLatestFrom(this._store.select(QuoteSelectors.selectFeatureState)),
      concatMap(([action, quoteState]) => {
        let nextNavigationPage = action.nextNavigationPage;

        // We override the digital application page in case the user is not eligible for it
        if (quoteState.isLegacyApplication === true) {
          nextNavigationPage = {
            pagePath: applicationLimitedOfferPagePath,
          };
        }

        const quoteIdQueryParameter: QueryStringParameter = {
          key: QueryStringParameterKey.QUOTE_ID,
          value: null,
        };

        const applicationIdQueryParameter: QueryStringParameter = {
          key: QueryStringParameterKey.APPLICATION_ID,
          value: action.applicationId,
        };

        return from(
          this._routerService.tryNavigateToPage(nextNavigationPage, [
            quoteIdQueryParameter,
            applicationIdQueryParameter,
          ]),
        ).pipe(
          map(() =>
            QuoteActions.lockQuote({
              applicationId: action.applicationId,
            }),
          ),
        );
      }),
    ),
  );
}
